import { Column, Row } from "@silevis/reactgrid";
import { useEffect, useState } from "react";
import { renameField } from "../../General/Helpers";
import useDataverseService from "../../services/DataverseService";
import { useDataChanged } from "../Contexts/DataChangedProvider";
import { useData } from "../Contexts/DataProvider";
import BaseTable from "./BaseTable";
import {
  budgetPriorityColumns,
  columnWidth,
  DIGITS_PREFIX,
  DIGITS_WITH_UNDERSCORE,
  isDigitsOnly,
  parseNumber,
  renameHeaderRow,
} from "./TableHelpers";

const sortColumns = (columns: Column[]): Column[] => {
  const isPriority = (col: Column) =>
    budgetPriorityColumns.includes(String(col.columnId));
  return [
    ...columns.filter(isPriority),
    ...columns
      .filter((c) => !isPriority(c))
      .sort((a, b) => {
        const aId = renameField(String(a.columnId));
        const bId = renameField(String(b.columnId));

        const aMatch = aId.match(DIGITS_WITH_UNDERSCORE);
        const bMatch = bId.match(DIGITS_WITH_UNDERSCORE);
        if (aMatch && bMatch) {
          const [, aPre, aSuf] = aMatch;
          const [, bPre, bSuf] = bMatch;
          if (aPre !== bPre) return aPre.localeCompare(bPre);
          if (aSuf && bSuf) return Number(aSuf) - Number(bSuf);
        }
        if (isDigitsOnly(aId) && isDigitsOnly(bId)) {
          return Number(aId) - Number(bId);
        }
        return aId.localeCompare(bId);
      }),
  ];
};

const buildColumns = (data: any[], visibleGroups?: string[]): Column[] => {
  const uniqueColumns = new Set<string>();
  data.forEach((item: any) => {
    Object.keys(item).forEach((key) => {
      if (
        key !== "cr864_budgetrecordid" &&
        key !== "@odata.etag" &&
        key !== "_ownerid_value" &&
        key !== "new_submitted"
      )
        uniqueColumns.add(key);
    });
  });
  let columnsArray: Column[] = [];
  budgetPriorityColumns.forEach((columnId) => {
    columnsArray.push({
      columnId,
      width: columnWidth(columnId),
      resizable: true,
    });
    uniqueColumns.delete(columnId);
  });

  uniqueColumns.forEach((key) => {
    const match = renameField(key).match(DIGITS_PREFIX);
    if (
      !visibleGroups ||
      visibleGroups.length === 0 ||
      (match && visibleGroups.includes(match[1]))
    ) {
      columnsArray.push({ columnId: key, width: 90, resizable: true });
    }
  });

  return sortColumns(columnsArray);
};

const buildRows = (
  data: any[],
  columns: Column[],
  submitted: boolean,
  centers: any[]
): Row[] => {
  const rowHeight = 35;
  const headerRow: any = renameHeaderRow(columns);
  const dataRows = data.map((row, idx) => ({
    rowId: idx,
    height: rowHeight,
    cells: columns.map((col) => {
      const value = row[col.columnId];
      const readOnly =
        budgetPriorityColumns.includes(col.columnId.toString()) ||
        submitted ||
        col.columnId === "submitted" ||
        renameField(String(col.columnId)).startsWith("524") ||
        renameField(String(col.columnId)).startsWith("527") ||
        renameField(String(col.columnId)).startsWith("sum");
      if (
        typeof value === "number" ||
        renameField(String(col.columnId)).match(DIGITS_WITH_UNDERSCORE)
      ) {
        return { type: "number", value, groupId: col.columnId, readOnly };
      } else if (col.columnId == "_new_costcenter_value") {
        const matchingCenter = centers.find(
          (center) => center.new_costcentersid === value
        );
        const displayText = matchingCenter?.new_name ?? "";
        return {
          type: "text",
          text: displayText,
          groupId: col.columnId,
          readOnly,
        };
      } else if (typeof value === "string") {
        return { type: "text", text: value, groupId: col.columnId, readOnly };
      } else {
        return { type: "text", text: "", groupId: col.columnId, readOnly };
      }
    }),
    id: row.cr864_budgetrecordid,
  }));
  return [headerRow, ...dataRows];
};

interface BudgetTableProps {
  submitted?: boolean;
  visibleGroups?: string[];
}

const BudgetTable = ({
  submitted = false,
  visibleGroups = [],
}: BudgetTableProps) => {
  const { data, setData, originalData } = useData();
  const { setDataChanged } = useDataChanged();
  const { getCostCenters } = useDataverseService();

  const [columns, setColumns] = useState<Column[]>([]);
  const [rows, setRows] = useState<Row[]>([]);
  const [costCenters, setCostCenters] = useState<any[]>([]);

  useEffect(() => {
    const fetchCenters = async () => {
      const centers = await getCostCenters();
      setCostCenters(centers);
    };
    fetchCenters();
  }, []);

  useEffect(() => {
    if (data.value && data.value.length > 0) {
      const cols = buildColumns(data.value, visibleGroups);
      setColumns(cols);
      setRows(buildRows(data.value, cols, submitted, costCenters));
    } else {
      setColumns([]);
      setRows([]);
    }
  }, [data, submitted, visibleGroups, costCenters]);

  const handleCellChange = (changedCells: any) => {
    if (submitted) {
      return;
    }
    const newData = [...data.value];
    changedCells.forEach(({ rowId, columnId, newCell }: any) => {
      if (newCell && !newCell.readOnly && newData[rowId]) {
        switch (newCell.type) {
          case "number":
            if (columnId in newData[rowId]) {
              (newData[rowId] as any)[columnId] = parseNumber(newCell.value);
            }
            break;
          case "text":
            if (columnId in newData[rowId]) {
              (newData[rowId] as any)[columnId] = newCell.text;
            }
            break;
        }
      }
    });
    setData({ ...data, value: newData });
    setRows(buildRows(newData, columns, submitted, costCenters));
    setDataChanged(true);
  };

  const modifyCellValues = (
    modifyBy: number,
    operation: "multiply" | "add" | "fixed",
    range: any[]
  ) => {
    if (
      !Array.isArray(range) ||
      range.length === 0 ||
      !range[0].columns ||
      !range[0].rows ||
      submitted
    ) {
      return;
    }

    const newData = [...data.value];
    for (let column of range[0].columns) {
      for (let row of range[0].rows) {
        let cell = row.cells.find((c: any) => c.groupId === column.columnId);
        if (cell && cell.type === "number" && !cell.readOnly) {
          const { groupId, value } = cell;
          const { rowId } = row;
          switch (operation) {
            case "multiply":
              if (groupId in newData[rowId]) {
                (newData[rowId] as any)[groupId] = value * modifyBy;
              }
              break;
            case "add":
              if (groupId in newData[rowId]) {
                (newData[rowId] as any)[groupId] =
                  Number(cell.value ?? 0) + Number(modifyBy ?? 0);
              }
              break;
          }
        }
      }
    }
    setData({ ...data, value: newData });
    setRows(buildRows(newData, columns, submitted, costCenters));
    setDataChanged(true);
  };

  return (
    <BaseTable
      columns={columns}
      setColumns={setColumns}
      rows={rows}
      stickyLeftColumns={9}
      onCellsChanged={handleCellChange}
      modifyCellValues={modifyCellValues}
    />
  );
};

export default BudgetTable;
